import { useDefaultPaymentMethod } from '../../financeiro/useDefaultPaymentMethod';
import { readLastPaymentMethod } from '../../vendas/helpers';
import { zonedDate } from '../../../utils/date-time';
// src/modules/assinaturas/estabelecimento/Periodos.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useAppContext } from '../../../contexts/AppContext';
import { AssinaturaPeriodoService } from './Service';

import PeriodosEditModal from './components/PeriodosEditModal';
import { Modal } from '../../../components/Modal';
import ScheduleView from '../../agenda/estabelecimento/view/ScheduleView';
import { getScheduleStatusLabel, getScheduleTone } from '../../agenda/cliente/List/helpers';

import {
    BillingStatusTag,
    canEditPeriodo,
    cantEditReason,
    EditState,
    formatCurrencyBRL,
    formatCivilDateBR,
    formatDateBR,
    Periodo,
    PlanoService,
    resolveServiceLabel,
} from './utils/periodosUtils';
import { Calendar, ChevronLeft, Minus, Plus, TriangleAlert } from 'lucide-react';

const periodoService = new AssinaturaPeriodoService();
const PAGE_SIZE = 6;

type UsageEntry = {
    label: string;
    serviceId?: string;
    editable: boolean;
    used: number;
    limit: number | null;
};

export default function AssinaturasPeriodos({
    assinatura,
    servicesDict,
    onChanged,
}: {
    assinatura: any;
    servicesDict: Record<string, string>;
    onChanged: () => void;
}) {
    const { errorSnackbar, successSnackbar } = useAppContext();
    const assinaturaId: string = assinatura?._id;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [periodos, setPeriodos] = useState<Periodo[]>([]);
    const [visible, setVisible] = useState(PAGE_SIZE);
    const [savingUsage, setSavingUsage] = useState(false);
    const [edit, setEdit] = useState<EditState>({ open: false });
    const [schedulesModal, setSchedulesModal] = useState<{
        open: boolean;
        periodo?: Periodo | null;
        loading?: boolean;
        schedules: any[];
        selectedSchedule?: any | null;
    }>({ open: false, schedules: [] });

    const loadPeriodos = useCallback(async () => {
        if (!assinaturaId) return;
        try {
            setError(false);
            const { data } = await periodoService.listarPorAssinatura(assinaturaId);
            setPeriodos(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Erro ao carregar períodos:', e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [assinaturaId]);

    useEffect(() => {
        loadPeriodos();
    }, [loadPeriodos]);

    // map serviços do plano (preferindo o snapshot da contratação)
    const planServicesMap = useMemo(() => {
        const plan = assinatura?.planoSnapshot || assinatura?.plano || assinatura?.plan;
        const list: PlanoService[] = plan?.services || [];
        const map = new Map<string, { serviceName?: string; quantity?: number; recurrence?: string }>();
        list.forEach((s) =>
            map.set(s.serviceId, {
                serviceName: s.serviceName,
                quantity: s.quantity,
                recurrence: s.recurrence,
            }),
        );
        return map;
    }, [assinatura]);

    // O periodo mais recente interessa mais: e nele que se registra uso e pagamento.
    const ordered = useMemo(
        () =>
            [...periodos].sort(
                (a, b) => new Date(b.inicio || 0).getTime() - new Date(a.inicio || 0).getTime(),
            ),
        [periodos],
    );

    const isAssinaturaAtiva = assinatura?.status === 1;

    const usageEntries = (row: Periodo): UsageEntry[] => {
        if (Array.isArray(row.servicos) && row.servicos.length) {
            return row.servicos.map((s) => ({
                serviceId: s.serviceId,
                editable: Boolean(s.serviceId),
                label: resolveServiceLabel(s.serviceId, s.serviceName, planServicesMap, servicesDict),
                used: s.used ?? 0,
                limit: typeof s.limit === 'number' ? s.limit : null,
            }));
        }

        if (Array.isArray(row.usos) && row.usos.length) {
            return row.usos.map((u) => ({
                serviceId: u.serviceId,
                editable: false,
                label: resolveServiceLabel(u.serviceId, u.nomeServico, planServicesMap, servicesDict),
                used: u.quantidadeUsada ?? 0,
                limit: typeof u.limite === 'number' ? u.limite : null,
            }));
        }

        return [];
    };

    const defaultPaymentMethod = useDefaultPaymentMethod();

    // abrir/fechar modal
    const openEdit = (row: Periodo) => {
        setEdit({
            open: true,
            periodo: row,
            // Sugere o valor previsto e a data de hoje, sem travar o ajuste.
            valorPago: typeof row.valorPago === 'number' && row.valorPago > 0 ? row.valorPago : row.valorPrevisto ?? 0,
            cobrancaStatus: 'pago',
            paidAt: row.paidAt ?? moment().toISOString(),
            paymentMethod: defaultPaymentMethod ?? readLastPaymentMethod(),
        });
    };
    const closeEdit = () => setEdit({ open: false });

    const openSchedules = async (row: Periodo) => {
        setSchedulesModal({ open: true, periodo: row, loading: true, schedules: [], selectedSchedule: null });

        try {
            const { data } = await periodoService.listarAgendamentos(row._id);
            setSchedulesModal({
                open: true,
                periodo: row,
                loading: false,
                schedules: Array.isArray(data) ? data : [],
                selectedSchedule: null,
            });
        } catch (e) {
            console.error('Erro ao carregar agendamentos do período:', e);
            errorSnackbar?.('Falha ao carregar agendamentos do período.');
            setSchedulesModal({ open: true, periodo: row, loading: false, schedules: [], selectedSchedule: null });
        }
    };

    const closeSchedules = () => setSchedulesModal({ open: false, schedules: [], selectedSchedule: null });

    const onAdjustUsage = async (periodoId: string, serviceId: string, delta: 1 | -1) => {
        if (savingUsage) return;
        setSavingUsage(true);
        try {
            const { data } = await periodoService.ajustarUso(periodoId, serviceId, delta);
            setPeriodos((current) => current.map((periodo) => (periodo._id === periodoId ? data : periodo)));
            successSnackbar?.(delta === 1 ? 'Uso adicionado.' : 'Uso removido.');
        } catch (e: any) {
            errorSnackbar?.(e?.response?.data?.message || 'Falha ao ajustar o uso do serviço.');
        } finally {
            setSavingUsage(false);
        }
    };

    // submit edição
    const onSaveBilling = async () => {
        if (!edit.periodo?._id) return;

        if (!canEditPeriodo(isAssinaturaAtiva, edit.periodo.cobrancaStatus)) {
            errorSnackbar?.(cantEditReason(isAssinaturaAtiva, edit.periodo.cobrancaStatus));
            return;
        }

        try {
            setSaving(true);
            await periodoService.atualizarCobranca(edit.periodo._id, {
                valorPago: edit.valorPago ?? 0,
                cobrancaStatus: 'pago',
                paidAt: edit.paidAt,
                paymentMethod: edit.paymentMethod,
            });
            closeEdit();
            await loadPeriodos();
            onChanged();
            successSnackbar?.('Pagamento registrado.');
        } catch (e: any) {
            console.error('Erro ao atualizar cobrança:', e);
            errorSnackbar?.(e?.response?.data?.message || 'Falha ao registrar o pagamento do período.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="sf-skeleton h-40" />
                <div className="sf-skeleton h-40" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os períodos</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={loadPeriodos}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!ordered.length) {
        return (
            <div className="sf-state">
                <Calendar aria-hidden />
                <h2>Nenhum período gerado</h2>
                <p>Os períodos de uso e cobrança desta assinatura aparecem aqui.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {ordered.slice(0, visible).map((row) => {
                const entries = usageEntries(row);
                const allowed = canEditPeriodo(isAssinaturaAtiva, row.cobrancaStatus);
                const paid = String(row.cobrancaStatus || '').toLowerCase() === 'pago';

                return (
                    <article key={row._id} className="sf-group ass-period">
                        <div className="ass-period-head">
                            <div className="min-w-0">
                                <h3 className="ass-title">
                                    {formatCivilDateBR(row.inicio)} a {formatCivilDateBR(row.fim)}
                                </h3>
                                <div className="ass-sub">
                                    {formatCurrencyBRL(row.valorPrevisto ?? 0)}
                                    {row.dueAt && ` · vence em ${formatCivilDateBR(row.dueAt)}`}
                                    {paid && ` · pago ${formatCurrencyBRL(row.valorPago ?? 0)} em ${formatDateBR(row.paidAt)}`}
                                </div>
                            </div>
                            <div className="sf-tags !mt-0 shrink-0 justify-end">
                                {row.ativo === true && (
                                    <span className="sf-tag" data-tone="blue">
                                        Atual
                                    </span>
                                )}
                                <BillingStatusTag status={row.cobrancaStatus} />
                            </div>
                        </div>

                        {entries.length > 0 ? (
                            <div className="ass-usage">
                                {entries.map((item, index) => {
                                    const percent =
                                        typeof item.limit === 'number' && item.limit > 0
                                            ? Math.min(Math.round((item.used / item.limit) * 100), 100)
                                            : null;

                                    return (
                                        <div key={item.serviceId || index} className="ass-usage-item">
                                            <div className="min-w-0 flex-1">
                                                <div className="ass-usage-line">
                                                    <span className="ass-text">{item.label}</span>
                                                    <span className="ass-sub shrink-0">
                                                        {item.used}
                                                        {typeof item.limit === 'number' ? ` de ${item.limit}` : ''}
                                                    </span>
                                                </div>
                                                {percent !== null && (
                                                    <div className="ass-usage-bar" aria-hidden>
                                                        <span style={{ width: `${percent}%` }} />
                                                    </div>
                                                )}
                                            </div>
                                            {item.editable && item.serviceId && (
                                                <div className="ass-stepper" role="group" aria-label={`Ajustar usos de ${item.label}`}>
                                                    <button
                                                        type="button"
                                                        disabled={savingUsage || item.used <= 0}
                                                        onClick={() => onAdjustUsage(row._id, item.serviceId!, -1)}
                                                        aria-label={`Remover um uso de ${item.label}`}
                                                    >
                                                        <Minus aria-hidden />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={savingUsage || typeof item.limit !== 'number' || item.used >= item.limit}
                                                        onClick={() => onAdjustUsage(row._id, item.serviceId!, 1)}
                                                        aria-label={`Adicionar um uso de ${item.label}`}
                                                    >
                                                        <Plus aria-hidden />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="ass-usage ass-sub">Sem usos registrados neste período.</div>
                        )}

                        <div className="ass-period-actions">
                            <button type="button" className="sf-link" onClick={() => openSchedules(row)}>
                                Ver agendamentos
                            </button>
                            {allowed && (
                                <button type="button" className="sf-link" onClick={() => openEdit(row)}>
                                    Registrar pagamento
                                </button>
                            )}
                        </div>
                    </article>
                );
            })}

            {ordered.length > visible && (
                <div className="flex justify-center">
                    <button type="button" className="sf-link" onClick={() => setVisible((current) => current + PAGE_SIZE)}>
                        Mostrar períodos anteriores ({ordered.length - visible})
                    </button>
                </div>
            )}

            {schedulesModal.open && (
                <Modal
                    title="Agendamentos do período"
                    subTitle={
                        schedulesModal.periodo
                            ? `${formatCivilDateBR(schedulesModal.periodo.inicio)} a ${formatCivilDateBR(schedulesModal.periodo.fim)}`
                            : undefined
                    }
                    onClose={closeSchedules}
                >
                    {schedulesModal.selectedSchedule ? (
                        <div className="space-y-3">
                            <button
                                type="button"
                                className="sf-back-button -ml-1"
                                onClick={() => setSchedulesModal((current) => ({ ...current, selectedSchedule: null }))}
                            >
                                <ChevronLeft aria-hidden />
                                Agendamentos
                            </button>
                            <ScheduleView schedule={schedulesModal.selectedSchedule} />
                        </div>
                    ) : schedulesModal.loading ? (
                        <div className="space-y-2" aria-busy="true">
                            <div className="sf-skeleton h-16" />
                            <div className="sf-skeleton h-16" />
                        </div>
                    ) : schedulesModal.schedules.length === 0 ? (
                        <div className="sf-state">
                            <Calendar aria-hidden />
                            <h2>Nenhum agendamento neste período</h2>
                        </div>
                    ) : (
                        <ul className="ass-schedules">
                            {schedulesModal.schedules.map((schedule) => {
                                const start = zonedDate(schedule?.startAt, schedule?.timezone);

                                return (
                                    <li key={schedule._id}>
                                        <button
                                            type="button"
                                            className="ass-schedule"
                                            onClick={() =>
                                                setSchedulesModal((current) => ({ ...current, selectedSchedule: schedule }))
                                            }
                                        >
                                            <span className="sf-date" aria-hidden>
                                                <span>{start.format('ddd').replace('.', '')}</span>
                                                <strong>{start.format('DD')}</strong>
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="ass-title block truncate">
                                                    <span className="sr-only">{start.format('dddd, DD/MM')} </span>
                                                    {start.format('HH:mm')} · {schedule?.service?.name || schedule?.servico?.name || 'Serviço'}
                                                    {schedule?.servicosExtras?.length ? ` +${schedule.servicosExtras.length}` : ''}
                                                </span>
                                                <span className="sf-tags">
                                                    <span className="sf-tag" data-tone={getScheduleTone(schedule?.status)}>
                                                        {getScheduleStatusLabel(schedule)}
                                                    </span>
                                                </span>
                                            </span>
                                            <span className="ass-price shrink-0">
                                                {formatCurrencyBRL(schedule?.totalPrice ?? schedule?.service?.price ?? 0)}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Modal>
            )}

            <PeriodosEditModal
                edit={edit}
                loading={saving}
                setEdit={setEdit}
                onClose={closeEdit}
                onSave={onSaveBilling}
            />
        </div>
    );
}
